/**
 * Strategy Lab — shared display formatters.
 * Small pure helpers used across the Lab tiles/tables. No dependencies.
 */

export function fmtUsd(value: number | null | undefined, opts?: { cents?: boolean }): string {
  if (value == null || Number.isNaN(value)) return '—';
  const cents = opts?.cents ?? Math.abs(value) < 100;
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  });
}

/** Signed dollars with an explicit + for positives (P&L display). */
export function fmtSignedUsd(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}${fmtUsd(Math.abs(value), { cents: true })}`;
}

export function fmtPct(value: number | null | undefined, digits = 1): string {
  if (value == null || Number.isNaN(value)) return '—';
  return `${value.toFixed(digits)}%`;
}

export function fmtNum(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—';
  if (typeof value === 'string') return value;
  if (Number.isNaN(value)) return '—';
  if (Math.abs(value) >= 1_000_000) return `${(value / 1_000_000).toFixed(2)}M`;
  if (Math.abs(value) >= 1_000) return value.toLocaleString('en-US');
  return String(Number(value.toFixed(4)));
}

/** Tailwind text color for a P&L number (emerald / rose / muted). */
export function pnlColorClass(value: number | null | undefined): string {
  if (value == null || value === 0 || Number.isNaN(value)) return 'text-muted-foreground';
  return value > 0 ? 'text-emerald-500' : 'text-rose-500';
}
